<template>
  <v-card
    data-testid="subsidy-child"
    border
  >
    <v-card-text>
      <div
        v-if="child.editing"
        class="mb-4"
      >
        <v-row class="fs-16">
          <LabeledTextfield
            v-model="child.first_name"
            :aria-label="$bct('Enter a first name')"
            :autofocus="autofocus"
            :dense="dense"
            :readonly="readonly"
            :schema-id="child.schema_id"
            cols="12"
            field="first_name"
            message="First name"
            sm="4"
            mandatory
          />
          <LabeledTextfield
            v-model="child.middle_name"
            :aria-label="$bct('Enter an optional middle name')"
            :dense="dense"
            :placeholder="$bct('Optional')"
            :readonly="readonly"
            :schema-id="child.schema_id"
            cols="12"
            field="middle_name"
            message="Middle name"
            sm="4"
          />
          <LabeledTextfield
            v-model="child.last_name"
            :aria-label="$bct('Enter a last name')"
            :dense="dense"
            :readonly="readonly"
            :schema-id="child.schema_id"
            cols="12"
            field="last_name"
            message="Last name"
            sm="4"
            mandatory
          />
        </v-row>
        <v-row class="fs-16">
          <LabeledDateOfBirth
            v-model="child.dob"
            :aria-label="$bct('Enter a date of birth')"
            :dense="dense"
            :readonly="readonly"
            :schema-id="child.schema_id"
            cols="12"
            data-cy="dob-select"
            field="dob"
            message="Date of birth"
            mandatory
          />
        </v-row>
        <v-row class="d-flex align-center">
          <v-col>
            <v-btn
              v-if="shouldShowCancelButton(child)"
              @click="emit('cancel')"
              :loading="processing"
              class="focus-very-visible"
              variant="outlined"
            >
              {{ $bct('Cancel') }}
            </v-btn>
          </v-col>
          <v-col class="d-flex justify-end">
            <v-btn
              @click="update()"
              :disabled="invalid"
              :loading="processing"
              :ripple="false"
              class="focus-very-visible me-3"
              color="primary"
              data-cy="done-button"
            >
              {{ $bct('Done') }}
            </v-btn>
          </v-col>
        </v-row>
      </div>
      <v-row
        v-else
        class="d-flex align-center"
      >
        <v-col class="d-flex align-center">
          <v-icon
            v-if="invalid"
            class="c-red me-3"
            icon="error_outline"
            size="26"
          />

          <div>
            <template v-if="child.first_name && child.last_name">
              <div
                v-text="[child.first_name, child.last_name].join(' ')"
                class="d-block fs-16 fw-600"
                data-cy="child-name"
              />
            </template>
            <template v-else>
              <div class="d-block fs-16 fw-600 c-red">
                {{ $bct('Incomplete name') }}
              </div>
            </template>
            <template v-if="child.dob">
              <div class="d-block">
                <LongDate
                  :date="child.dob"
                  class="fs-16"
                  data-cy="child-dob"
                  timezone="UTC"
                />
              </div>
            </template>
            <template v-else>
              <div class="d-block fs-16 fw-600 c-red">
                {{ $bct('Missing date of birth') }}
              </div>
            </template>
          </div>
        </v-col>
        <v-col cols="auto">
          <template v-if="hasExistingSubsidy">
            <v-tooltip location="top">
              <template #activator="{ props: tooltipProps }">
                <v-icon
                  v-bind="tooltipProps"
                  color="primary"
                  icon="help_outline"
                />
              </template>
              <template #default>
                {{
                  $bct(
                    'This child cannot be edited because they are associated with an existing application.',
                  )
                }}<br />
                {{ $bct('Please reach out to an administrator for assistance') }}
              </template>
            </v-tooltip>
          </template>
          <v-btn
            v-else
            @click="child.editing = true"
            :ripple="false"
            class="focus-very-visible"
            color="primary"
            variant="text"
          >
            {{ $bct('Edit') }}
          </v-btn>
        </v-col>
        <v-col cols="auto">
          <v-btn
            @click="child.included = !child.included"
            :loading="processing"
            :ripple="false"
            :variant="child.included ? 'flat' : 'outlined'"
            class="focus-very-visible"
            color="primary"
          >
            <v-icon start>
              {{ child.included ? 'check_box' : 'check_box_outline_blank' }}
            </v-icon>
            <span>
              {{ $bct(child.included ? 'Included in application' : 'Include in application') }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup>
import _ from 'lodash';
import LabeledDateOfBirth from '@/shared/components/form/LabeledDateOfBirth.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import LongDate from '@/shared/components/LongDate.vue';
import useApi from '@/shared/composables/useApi';

const props = defineProps({
  autofocus: Boolean,
  dense: Boolean,
  hasExistingSubsidy: { default: false, type: Boolean },
  index: { type: Number, default: null },
  initialChild: { default: null, type: Object },
  preventUpdate: Boolean,
  readonly: Boolean,
  removeDisabled: { default: false, type: Boolean },
});

const emit = defineEmits(['cancel', 'change', 'removal']);

const { api } = useApi();
const $bct = inject('translator');

const child = ref(props.initialChild);
const processing = ref(false);

const invalid = computed(() => {
  return (
    !child.value.first_name || !child.value.last_name || !child.value.dob || !child.value.schema_id
  );
});

const isPersisted = (child) => !_.isNil(child.id);

const shouldShowCancelButton = (child) => (child.editing && isPersisted(child)) || props.index > 0;

const update = async () => {
  processing.value = true;
  if (child.value.id) {
    const resp = await api.child.update(child.value.id, child.value);
    const updatedChild = resp.data;
    updatedChild.included = true;
    updatedChild.editing = false;
    child.value = updatedChild;
    emit('change', child.value);
    processing.value = false;
  } else {
    const resp = await api.child.create(child.value);

    if (!resp?.data) {
      processing.value = false;
      return;
    }

    const newChild = resp.data;
    newChild.included = true;
    newChild.editing = false;
    child.value = newChild;
    emit('change', child.value);
    processing.value = false;
  }
};

watch(
  () => props.initialChild,
  (newVal) => {
    child.value = newVal;
  },
);
</script>
